import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

/**
 * Shared layout for all three legal pages.
 * Renders a sticky sidebar TOC on desktop, linear list on mobile.
 *
 * sections: [{ id: "section-id", title: "Section Title", content: ReactNode }]
 */
export default function LegalPage({ title, subtitle, lastUpdated, breadcrumb, sections }) {
  const [activeId, setActiveId] = useState(sections[0]?.id);
  const observerRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => { if (e.isIntersecting) setActiveId(e.target.id); });
      },
      { rootMargin: "-20% 0px -70% 0px" }
    );
    sections.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observerRef.current.observe(el);
    });
    return () => observerRef.current?.disconnect();
  }, [sections]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) { el.scrollIntoView({ behavior: "smooth", block: "start" }); }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Page header */}
      <div className="bg-[#0F172A] text-white">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <nav className="flex items-center gap-2 text-xs text-gray-400 mb-4">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-gray-300">{breadcrumb}</span>
          </nav>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">{title}</h1>
          {subtitle && <p className="mt-3 text-gray-400 max-w-2xl text-sm leading-relaxed">{subtitle}</p>}
          {lastUpdated && (
            <p className="mt-4 text-xs text-gray-500">Last updated: {lastUpdated}</p>
          )}
        </div>
      </div>

      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
        <div className="lg:grid lg:grid-cols-[260px_1fr] lg:gap-12">

          {/* Sidebar TOC */}
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              <p className="text-[11px] font-bold uppercase tracking-widest text-gray-400 mb-4">Contents</p>
              <nav className="flex flex-col gap-0.5">
                {sections.map(({ id, title: t }) => (
                  <button key={id} onClick={() => scrollTo(id)}
                    className={`text-left text-sm px-3 py-2 rounded-lg transition-all ${
                      activeId === id
                        ? "bg-[#EEF2FF] text-[#4F46E5] font-medium"
                        : "text-gray-500 hover:text-gray-900 hover:bg-gray-50"
                    }`}>
                    {t}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main content */}
          <main>
            {/* Mobile TOC */}
            <details className="lg:hidden mb-8 border border-gray-200 rounded-2xl overflow-hidden">
              <summary className="px-5 py-4 cursor-pointer font-medium text-sm text-[#0F172A] bg-gray-50">
                Table of Contents
              </summary>
              <nav className="px-5 py-3 grid gap-1">
                {sections.map(({ id, title: t }) => (
                  <button key={id} onClick={() => { scrollTo(id); document.querySelector("details")?.removeAttribute("open"); }}
                    className="text-left text-sm text-[#4F46E5] py-1 hover:underline">
                    {t}
                  </button>
                ))}
              </nav>
            </details>

            {/* Sections */}
            <div className="prose prose-slate max-w-none legal-content">
              {sections.map(({ id, title: t, content }) => (
                <section key={id} id={id} className="mb-10 scroll-mt-24">
                  <h2 className="text-xl font-semibold text-[#0F172A] mb-4 pb-3 border-b border-gray-100">{t}</h2>
                  <div className="text-gray-600 text-[15px] leading-relaxed space-y-3">{content}</div>
                </section>
              ))}
            </div>

            {/* Footer note */}
            <div className="mt-12 pt-8 border-t border-gray-100 flex flex-wrap gap-4 text-sm text-gray-500">
              <Link to="/terms"         className="hover:text-[#4F46E5] hover:underline">Terms &amp; Conditions</Link>
              <Link to="/privacy"       className="hover:text-[#4F46E5] hover:underline">Privacy Policy</Link>
              <Link to="/podcast-terms" className="hover:text-[#4F46E5] hover:underline">Podcast Terms</Link>
              <Link to="/contact"       className="hover:text-[#4F46E5] hover:underline">Contact Us</Link>
            </div>
          </main>
        </div>
      </div>

      <style>{`
        .legal-content p { margin-bottom: 0.75rem; }
        .legal-content ul { list-style: disc; padding-left: 1.5rem; margin: 0.75rem 0; }
        .legal-content ul li { margin-bottom: 0.35rem; }
        .legal-content ol { list-style: decimal; padding-left: 1.5rem; margin: 0.75rem 0; }
        .legal-content ol li { margin-bottom: 0.35rem; }
        .legal-content strong { color: #1e293b; font-weight: 600; }
        .legal-content a { color: #4F46E5; text-decoration: underline; }
        .legal-content a:hover { color: #4338CA; }
        .legal-content .highlight-box { background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 12px; padding: 1rem 1.25rem; margin: 1rem 0; }
      `}</style>
    </div>
  );
}
